import { ApiService } from '~/services/api/api'
import type { ShareableEntityList } from '~/types/ShareableEntityList'
import type { Test } from '~/types/Test'
import type {
  PopulatedTestAttempt,
  QuestionAttempt,
  TestAttempt,
  TestAttemptGrade,
  TestAttemptWithGradeAndCandidate,
} from '~/types/TestAttempt'
import type { UploadedFile } from '~/types/UploadedFile'
import { FileUploadService } from '~/services/api/file/file-upload'
import type { SubmissionsOverview } from '~/types/Submission'
import type { TestAttemptBatchSelectionParameters } from '~/types/TestAttemptBatchSelectionParameters'

export class TestService {
  private readonly apiService = new ApiService()
  private readonly fileUploadService = new FileUploadService()
  private readonly fileSubmissionChunkSize = 1
  private readonly fileSubmissionUploadConcurrency = 1

  async getTests(queryParameters: Record<string, string> = {}) {
    const response = await this.apiService.get<Test[]>('/tests', {
      params: queryParameters,
    })
    return response
  }

  async getTestById(testId: string, relations?: string[]) {
    const response = await this.apiService.get<Test>(`tests/${testId}`, {
      useFallbackAuth: true,
      params: {
        relations,
      },
    })
    return response
  }

  // TODO: add auth to deep link page and then remove fallback auth from this method
  async getWorkspaceTests(workspaceId: string) {
    const response = await this.apiService.get<ShareableEntityList<Test>>(
      `/workspaces/${workspaceId}/tests`,
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async postTest(workspaceId: string, body: Partial<Test>) {
    const response = await this.apiService.post<Test, Partial<Test>>(
      `/workspaces/${workspaceId}/tests`,
      body,
    )
    return response
  }

  async updateTestById(
    workspaceId: string,
    testId: string,
    body: Partial<Test>,
  ) {
    const response = await this.apiService.patch<Test, Partial<Test>>(
      `/workspaces/${workspaceId}/tests/${testId}`,
      body,
    )
    return response
  }

  async deleteTestById(workspaceId: string, testId: string) {
    const response = await this.apiService.delete<undefined>(
      `/workspaces/${workspaceId}/tests/${testId}`,
    )
    return response
  }

  async getWorkspaceTestUpdates(workspaceId: string, updatedAtGt: string) {
    const response = await this.apiService.get<ShareableEntityList<Test>>(
      `/workspaces/${workspaceId}/tests`,
      {
        params: {
          updatedAtGt,
        },
      },
    )
    return response
  }

  async createTestAttempt(testId: string, body: Partial<TestAttempt>) {
    const response = await this.apiService.post<Test, Partial<TestAttempt>>(
      `/tests/${testId}/attempts`,
      body,
    )
    return response
  }

  async updateTestAttempt(
    testId: string,
    testAttemptId: string,
    body: Partial<TestAttempt>,
  ) {
    const response = await this.apiService.patch<Test, Partial<TestAttempt>>(
      `/tests/${testId}/attempts/${testAttemptId}`,
      body,
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async deleteTestAttempt(testId: string, testAttemptId: string) {
    const response = await this.apiService.delete<undefined>(
      `/tests/${testId}/attempts/${testAttemptId}`,
    )
    return response
  }

  async batchDeleteTestAttempts(
    testId: string,
    selection: TestAttemptBatchSelectionParameters,
  ) {
    const response = await this.apiService.post<
      {
        successfulCount: number
        failedCount: number
        totalCount: number
      },
      TestAttemptBatchSelectionParameters
    >(`/tests/${testId}/attempts/batch-delete`, selection)
    return response
  }

  async countEligibleFileSubmissionsForRetry(
    testId: string,
    selection: TestAttemptBatchSelectionParameters,
  ) {
    const response = await this.apiService.post<
      { eligibleCount: number; totalCount: number },
      TestAttemptBatchSelectionParameters
    >(
      `/tests/${testId}/attempts/file-submissions/batch-retry/eligible-count`,
      selection,
    )
    return response
  }

  async batchRetryFileSubmissionConversion(
    testId: string,
    selection: TestAttemptBatchSelectionParameters,
  ) {
    const response = await this.apiService.post<
      {
        successfulCount: number
        skippedCount: number
        failedCount: number
        totalCount: number
      },
      TestAttemptBatchSelectionParameters
    >(`/tests/${testId}/attempts/file-submissions/batch-retry`, selection)
    return response
  }

  async createQuestionAttempt(
    questionId: string,
    body: Partial<QuestionAttempt>,
  ) {
    const response = await this.apiService.post<Test, Partial<QuestionAttempt>>(
      `/questions/${questionId}/attempts`,
      body,
    )
    return response
  }

  async updateQuestionAttempt(
    questionId: string,
    questionAttemptId: string,
    body: Partial<QuestionAttempt>,
  ) {
    const response = await this.apiService.patch<
      Test,
      Partial<QuestionAttempt>
    >(`/questions/${questionId}/attempts/${questionAttemptId}`, body, {
      useFallbackAuth: true,
    })
    return response
  }

  async deleteQuestionAttempt(questionId: string, questionAttemptId: string) {
    const response = await this.apiService.delete<undefined>(
      `/questions/${questionId}/attempts/${questionAttemptId}`,
    )
    return response
  }

  async initialiseTestAttempt(testId: string, candidateId: string) {
    const response = await this.apiService.post<
      {
        testAttempt: TestAttempt
        questionAttempts: QuestionAttempt[]
      },
      { candidateId: string }
    >(
      `/tests/${testId}/attempts/initialize`,
      {
        candidateId,
      },
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async getTestAttemptsByCandidateId(testId: string, candidateId: string) {
    const response = await this.apiService.get<TestAttempt[]>(
      `/tests/${testId}/attempts/candidate/${candidateId}`,
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async getTestAttemptsByTestId(testId: string) {
    const response = await this.apiService.get<
      TestAttemptWithGradeAndCandidate[]
    >(`/tests/${testId}/attempts`)
    return response
  }

  async getTestAttemptById(testId: string, testAttemptId: string) {
    const response = await this.apiService.get<PopulatedTestAttempt>(
      `/tests/${testId}/attempts/${testAttemptId}`,
    )
    return response
  }

  async requestTestAttemptGrade(testId: string, testAttemptId: string) {
    const response = await this.apiService.post<string, undefined>(
      `/tests/${testId}/attempts/${testAttemptId}/request/grade`,
      undefined,
    )
    return response
  }

  async requestTestAttemptReGrade(testId: string, testAttemptId: string) {
    const response = await this.apiService.post<string, undefined>(
      `/tests/${testId}/attempts/${testAttemptId}/request/re-grade`,
      undefined,
    )
    return response
  }

  async retryFileSubmissionConversion(testId: string, testAttemptId: string) {
    const response = await this.apiService.post<string, undefined>(
      `/tests/${testId}/attempts/${testAttemptId}/file-submissions/retry`,
      undefined,
    )
    return response
  }

  async requestTestAttemptGradeAll(testId: string, regrade: boolean = false) {
    const regradeQuery = regrade ? 'true' : 'false'
    const response = await this.apiService.post<string, undefined>(
      `/tests/${testId}/attempts/grade?regrade=${regradeQuery}`,
      undefined,
    )
    return response
  }

  // TEST ATTEMPT SESSIONS ========================================================
  async getTestAttemptSession(
    testId: string,
    candidateId: string,
    assessmentIds: string[] = [],
    ltiDeepLinkId?: string,
  ) {
    const response = await this.apiService.post<
      {
        sessionId: string
        testAttempt: TestAttempt
        questionAttempts: QuestionAttempt[]
      },
      { candidateId: string; ltiDeepLinkId?: string }
    >(
      `/tests/${testId}/attempts/sessions`,
      {
        candidateId,
        ltiDeepLinkId,
      },
      {
        useFallbackAuth: true,
        params: {
          a: assessmentIds,
        },
      },
    )
    return response
  }

  async startTestAttemptSession(
    testId: string,
    sessionId: string,
    eventAttemptId?: string,
  ) {
    const response = await this.apiService.post<undefined, undefined>(
      `/tests/${testId}/attempts/sessions/${sessionId}/start`,
      undefined,
      {
        useFallbackAuth: true,
        params: { eventAttempt: eventAttemptId },
      },
    )
    return response
  }

  async completeTestAttemptSession(testId: string, sessionId: string) {
    const response = await this.apiService.post<undefined, undefined>(
      `/tests/${testId}/attempts/sessions/${sessionId}/complete`,
      undefined,
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async reuseTestAttemptForSession(
    testId: string,
    sessionId: string,
    testAttemptId: string,
    eventAttemptId?: string,
  ) {
    const response = await this.apiService.post<
      undefined,
      { testAttemptId: string }
    >(
      `/tests/${testId}/attempts/sessions/${sessionId}/reuse-test-attempt`,
      {
        testAttemptId,
      },
      {
        useFallbackAuth: true,
        params: { eventAttempt: eventAttemptId },
      },
    )
    return response
  }

  async uploadGradingCategoryContextFiles(
    workspaceId: string,
    testId: string,
    categoryId: string,
    files: File[],
  ) {
    const response = await this.fileUploadService.uploadFilesToEndpoint<
      {
        testId: string
        gradingCategoryId: string
        file: UploadedFile
      }[]
    >(
      `/workspaces/${workspaceId}/tests/${testId}/test-grading-categories/${categoryId}/context-files`,
      files,
    )
    return response
  }

  async deleteGradingCategoryContextFiles(
    workspaceId: string,
    testId: string,
    categoryId: string,
  ) {
    const response = await this.apiService.delete<undefined>(
      `/workspaces/${workspaceId}/tests/${testId}/test-grading-categories/${categoryId}/context-files`,
    )
    return response
  }

  async uploadTestContextFiles(testId: string, files: File[]) {
    const response = await this.fileUploadService.uploadFilesToEndpoint<
      {
        testId: string
        file: UploadedFile
      }[]
    >(`/tests/${testId}/context-files`, files)
    return response
  }

  async deleteTestContextFiles(testId: string) {
    const response = await this.apiService.delete<undefined>(
      `/tests/${testId}/context-files`,
    )
    return response
  }

  async uploadFileSubmissions(
    testId: string,
    files: File[] | FileList,
    organization: string,
    groupIds: string[] = [],
    {
      identifier,
      eventAttemptId,
      ltiDeepLinkId,
      batchId,
      onProgress,
    }: {
      identifier?: string
      eventAttemptId?: string
      ltiDeepLinkId?: string
      batchId?: string
      onProgress?: (uploadedCount: number, totalCount: number) => void
    } = {},
  ) {
    const filesArray = Array.from(files)
    const payload: {
      organization: string
      groupIds: string
      identifier?: string
      eventAttemptId?: string
      ltiDeepLinkId?: string
      batchId?: string
      totalFiles?: string
    } = {
      organization,
      groupIds: groupIds.join(','),
      // Send the grand total for the whole upload on every chunked request.
      // Uploads are chunked (fileSubmissionChunkSize) into separate requests,
      // so without this the backend would only ever see one chunk's file count
      // and mis-route large batches to the priority grading queue.
      totalFiles: String(filesArray.length),
    }

    if (identifier) {
      payload.identifier = identifier
    }

    if (eventAttemptId) {
      payload.eventAttemptId = eventAttemptId
    }

    if (ltiDeepLinkId) {
      payload.ltiDeepLinkId = ltiDeepLinkId
    }

    if (batchId) {
      payload.batchId = batchId
    }
    if (filesArray.length === 0) {
      return {
        data: [],
        error: null,
        status: 200,
        statusText: 'OK',
      }
    }

    const fileBatches: File[][] = []
    for (let i = 0; i < filesArray.length; i += this.fileSubmissionChunkSize) {
      fileBatches.push(filesArray.slice(i, i + this.fileSubmissionChunkSize))
    }

    const responses: {
      data:
        | {
            testAttempt: { id: string }
          }[]
        | null
      error: Error | null
      status: number
      statusText: string
    }[] = new Array(fileBatches.length)
    let uploadedCount = 0

    let nextBatchIndex = 0
    const workerCount = Math.min(
      this.fileSubmissionUploadConcurrency,
      fileBatches.length,
    )

    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        while (nextBatchIndex < fileBatches.length) {
          const batchIndex = nextBatchIndex++
          const currentBatch = fileBatches[batchIndex]

          responses[batchIndex] =
            await this.fileUploadService.uploadFilesToEndpoint<
              {
                testAttempt: { id: string }
              }[]
            >(
              `/tests/${testId}/attempts/file-submissions`,
              currentBatch,
              payload,
              {
                useFallbackAuth: true,
              },
            )

          if (responses[batchIndex]?.error === null) {
            uploadedCount += responses[batchIndex]?.data?.length ?? 0
            onProgress?.(
              Math.min(uploadedCount, filesArray.length),
              filesArray.length,
            )
          }
        }
      }),
    )

    const firstFailedResponse = responses.find(
      (uploadResponse) => uploadResponse.error !== null,
    )

    if (firstFailedResponse) {
      return firstFailedResponse
    }

    return {
      data: responses.flatMap((uploadResponse) => uploadResponse.data || []),
      error: null,
      status: responses[0].status,
      statusText: responses[0].statusText,
    }
  }

  async getTestAttemptGrade(testAttemptGradeId: string) {
    const response = await this.apiService.get<TestAttemptGrade>(
      `/test-attempt-grades/${testAttemptGradeId}`,
    )
    return response
  }

  async getResultsCsvLink(testId: string, withRemarks: boolean) {
    const response = await this.apiService.get<string>(
      `/tests/${testId}/attempts/grades/results/csv?withRemarks=${withRemarks}`,
    )
    return response
  }

  async getGradingVariabilityResultsCsvLink(testId: string) {
    const response = await this.apiService.get<string>(
      `/tests/${testId}/attempts/grades/results/csv/grading-variability`,
    )
    return response
  }

  async getIndividualResultsCsvLink(
    testId: string,
    testAttemptGradeId: string,
    withRemarks: boolean,
  ) {
    const response = await this.apiService.get<string>(
      `/tests/${testId}/attempts/grades/${testAttemptGradeId}/results/csv?withRemarks=${withRemarks}`,
    )
    return response
  }

  async getTestSubmissionsOverview(testId: string) {
    const response = await this.apiService.get<SubmissionsOverview>(
      `/tests/${testId}/attempts-overview`,
    )
    return response
  }

  async lockTest(testId: string, lockedBy: string) {
    const response = await this.apiService.post<
      { lockedBy: string; lockedAt: string },
      { lockedBy: string }
    >(`/tests/${testId}/lock`, { lockedBy })
    return response
  }

  async unlockTest(testId: string) {
    const response = await this.apiService.post<string, undefined>(
      `/tests/${testId}/unlock`,
      undefined,
    )
    return response
  }

  async exportTest(testId: string, isFileSubmissionTest: boolean) {
    const response = await this.apiService.get<unknown>(
      `tests/${testId}/export?${isFileSubmissionTest ? 'fileSubmissionTest=true' : 'fileSubmissionTest=false'}`,
    )
    return response
  }
}
